/**
 * BackendSubscriptionChecker Module
 * Checks user subscription status using the existing backend API
 * The backend already has access to Firestore through Firebase Admin SDK
 */

export class BackendSubscriptionChecker {
    constructor() {
        this.isReady = false;
        this.isSubscribed = false;
        this.subscriptionData = null;
        this.userId = null;
        
        this.initPromise = this.initialize();
    }
    
    async initialize() {
        if (this.isReady) return;
        
        console.log("[BackendSubscriptionChecker] Initializing...");
        
        // Set up authentication listener if Firebase Auth is available
        if (window.firebaseAuth) {
            this.setupAuthListener();
        } else {
            // Listen for Firebase to become ready
            window.addEventListener("firebaseReady", (event) => {
                if (event.detail.auth) {
                    this.setupAuthListener();
                }
            }, { once: true });
        }
        
        // Handle a post-Stripe-redirect return (success / cancel URL params).
        this.handleReturnFromCheckout();
        
        this.isReady = true;
    }
    
    setupAuthListener() {
        if (!window.firebaseAuth) {
            console.log("[BackendSubscriptionChecker] Firebase Auth not available");
            return;
        }
        
        // Use the existing Firebase Auth to detect when user logs in/out
        window.firebaseAuth.onAuthStateChanged(async (user) => {
            if (user) {
                console.log("[BackendSubscriptionChecker] User authenticated:", user.uid);
                this.userId = user.uid;
                
                // Check subscription status from backend (only on login)
                await this.checkSubscriptionStatus();
                
                // If a checkout was interrupted by the sign-in gate, resume it.
                this._resumePendingCheckout();
            } else {
                console.log("[BackendSubscriptionChecker] No user authenticated - performing complete cleanup");
                
                // Perform complete cleanup on sign-out
                this.performCompleteCleanup();
            }
        });
    }
    
    // Complete cleanup function for sign-out
    performCompleteCleanup() {
        console.log("[BackendSubscriptionChecker] Performing complete cleanup on sign-out");
        
        // Clear all subscription data
        this.userId = null;
        this.isSubscribed = false;
        this.subscriptionData = null;
        
        // Clear global references
        if (window.backendSubscriptionChecker) {
            window.backendSubscriptionChecker.isSubscribed = false;
            window.backendSubscriptionChecker.subscriptionData = null;
        }
        
        // Clear any cached subscription status
        if (window.userIsSubscribed !== undefined) {
            window.userIsSubscribed = false;
        }
        
        // Remove ALL subscription-related modals from DOM
        this.removeAllSubscriptionModals();
        
        // Clean up all subscription buttons
        this.cleanupAllButtons();
        
        // Update UI to reflect signed-out state
        this.updateUI();
    }
    
    // Remove all subscription-related modals from the DOM
    removeAllSubscriptionModals() {
        console.log("[BackendSubscriptionChecker] Removing all subscription modals from DOM");
        
        // List of modal IDs to remove
        const modalIds = [
            'subscribedModal',
            'subscriptionModal', 
            'subscriptionInfoModal',
            'subscriptionPlansModal',
            'sub-plans-modal',
            'sub-status-modal'
        ];
        
        modalIds.forEach(modalId => {
            const modal = document.getElementById(modalId);
            if (modal) {
                // Close Bootstrap modal if it's open
                if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
                    try {
                        const bsModal = bootstrap.Modal.getInstance(modal);
                        if (bsModal) {
                            bsModal.hide();
                            bsModal.dispose();
                        }
                    } catch (e) {
                        console.log(`[BackendSubscriptionChecker] Error closing modal ${modalId}:`, e);
                    }
                }
                
                // Remove the modal element from DOM
                modal.remove();
                console.log(`[BackendSubscriptionChecker] Removed modal: ${modalId}`);
            }
        });
        
        // Also remove any Bootstrap-created backdrops that might be lingering.
        // Only target backdrops WITHOUT an id: the Tuscan-dusk lab modals
        // (#modal-auth, #modal-switch, #modal-subscribe, #modal-quote) also carry
        // the .modal-backdrop class, and a bare querySelectorAll('.modal-backdrop')
        // deleted #modal-auth on the signed-out path — making "Enter the Lab" a no-op.
        const backdrops = document.querySelectorAll('.modal-backdrop:not([id])');
        backdrops.forEach(backdrop => backdrop.remove());
        
        // Remove any modal-open class from body
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('padding-right');
        document.body.style.removeProperty('overflow');
    }
    
    // Clean up all subscription buttons - more thorough cleanup
    cleanupAllButtons() {
        console.log("[BackendSubscriptionChecker] Cleaning up all subscription buttons");
        
        // Find all subscribe buttons
        const subscribeButtons = document.querySelectorAll('#subscribeButton, .subscribe-btn, button[data-action="subscribe"]');
        
        subscribeButtons.forEach(button => {
            // Remove ALL event listeners by cloning and replacing the button
            // This ensures complete removal of all event listeners
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
            
            // Reset button to unsubscribed state
            newButton.innerHTML = '<i class="fas fa-crown"></i> Subscribe';
            newButton.classList.remove('btn-success', 'subscribed');
            newButton.classList.add('btn-warning');
            newButton.disabled = false; // Ensure button is enabled
            
            // Remove any stored handler references
            delete newButton._backendSubscriptionHandler;
            
            console.log("[BackendSubscriptionChecker] Reset button to unsubscribed state");
        });
    }
    
    async checkSubscriptionStatus() {
        // Fall back to the live Firebase user so this can be called on demand
        // (e.g. from the quota gate before blocking a possible subscriber),
        // not only from the auth-state listener.
        if (!this.userId) {
            this.userId = window.firebaseAuth?.currentUser?.uid || null;
        }
        if (!this.userId) {
            console.log("[BackendSubscriptionChecker] No user ID, skipping check");
            return;
        }
        
        try {
            console.log("[BackendSubscriptionChecker] Checking subscription status from backend...");

            // Attach the Firebase ID token so the backend can authenticate the
            // user IMMEDIATELY, without waiting for the Flask session to be
            // established. This is the fix for subscribers being wrongly blocked
            // right after an Apple/Google sign-in: the session-sync round-trip
            // may not have completed yet, so the cookie alone identifies nobody.
            // The backend's get_authenticated_user_id() checks the Bearer token
            // FIRST, so a freshly signed-in subscriber resolves correctly here
            // instead of getting a false "not subscribed" -> "No questions
            // remaining". Falls back to the cookie if the token isn't available.
            const headers = { 'Content-Type': 'application/json' };
            try {
                const idToken =
                    await window.firebaseAuth?.currentUser?.getIdToken?.();
                if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
            } catch (tokenErr) {
                console.warn(
                    "[BackendSubscriptionChecker] Could not get ID token, falling back to session cookie:",
                    tokenErr,
                );
            }

            // Call the backend endpoint that checks Firestore.
            // The body userId is ignored server-side (identity always comes from
            // the verified token / session) but kept for log readability.
            const response = await fetch('/api/firestore-subscription-status', {
                method: 'POST',
                credentials: 'include',  // Changed from 'same-origin' to 'include' for Safari compatibility
                headers,
                body: JSON.stringify({
                    userId: this.userId  // Send the current user's Firebase UID
                })
            });
            
            if (!response.ok) {
                console.error("[BackendSubscriptionChecker] Failed to fetch subscription status:", response.status);
                return;
            }
            
            const data = await response.json();
            console.log("[BackendSubscriptionChecker] Subscription response:", data);
            
            // Update subscription status based on backend response
            this.isSubscribed = data.isSubscribed === true;
            this.subscriptionData = data.subscription || null;
            
            // Log the specific status for debugging
            if (this.subscriptionData && this.subscriptionData.status) {
                console.log("[BackendSubscriptionChecker] Subscription status field:", this.subscriptionData.status);
            }
            
            // The backend checks if latestSubscription.status === 'complete'
            if (this.isSubscribed) {
                console.log("[BackendSubscriptionChecker] ✓ User has active subscription!");
            } else {
                console.log("[BackendSubscriptionChecker] User does not have active subscription");
            }
            
            // Update UI elements
            this.updateUI();
            
        } catch (error) {
            console.error("[BackendSubscriptionChecker] Error checking subscription:", error);
            this.isSubscribed = false;
        }
    }
    
    updateUI() {
        console.log("[BackendSubscriptionChecker] Updating UI - isSubscribed:", this.isSubscribed);
        
        // Update subscription button
        this.updateSubscriptionButton();
        
        // Hide/show question counter
        this.updateQuestionCounter();
        
        // Dispatch event for other modules to listen to
        window.dispatchEvent(new CustomEvent('subscriptionStatusChecked', {
            detail: {
                isSubscribed: this.isSubscribed,
                subscriptionData: this.subscriptionData,
                userId: this.userId
            }
        }));
    }
    
    updateSubscriptionButton() {
        // Get references to the two locations
        const actionBarContainer = document.querySelector('.standalone-action-buttons-container');
        const profileContainer = document.getElementById('profile-subscription-container');
        const mainSubscribeButton = document.getElementById('subscribeButton');
        
        if (this.isSubscribed) {
            // User has valid subscription - move button to Profile screen
            console.log("[BackendSubscriptionChecker] User is subscribed - moving button to Profile");
            this.relocateButtonToProfile(mainSubscribeButton, profileContainer, actionBarContainer);
        } else {
            // User is not subscribed - keep/restore button in action bar
            console.log("[BackendSubscriptionChecker] User is not subscribed - keeping button in action bar");
            this.relocateButtonToActionBar(mainSubscribeButton, profileContainer, actionBarContainer);
        }
        
        // Find and update all subscribe buttons (including relocated ones)
        const subscribeButtons = document.querySelectorAll('#subscribeButton, .subscribe-btn, button[data-action="subscribe"]');
        
        subscribeButtons.forEach(button => {
            // FIRST: Complete cleanup - remove ALL existing event listeners by cloning
            const newButton = button.cloneNode(true);
            button.parentNode.replaceChild(newButton, button);
            
            // Clear any stored handler references
            delete newButton._backendSubscriptionHandler;
            
            // THEN: Set up button based on subscription status
            if (this.isSubscribed) {
                // Update button to show subscribed status
                newButton.innerHTML = '<i class="fas fa-check-circle"></i> Subscribed';
                newButton.classList.remove('btn-warning', 'btn-primary');
                newButton.classList.add('btn-success', 'subscribed');
                newButton.disabled = false;
                
                // Create and store the handler
                newButton._backendSubscriptionHandler = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.openStatus();
                };
                
                // Add event listener with high priority (capture phase)
                newButton.addEventListener('click', newButton._backendSubscriptionHandler, true);
            } else {
                // Reset to unsubscribed state
                newButton.innerHTML = '<i class="fas fa-crown"></i> Subscribe';
                newButton.classList.remove('btn-success', 'subscribed');
                newButton.classList.add('btn-warning');
                newButton.disabled = false;
                
                // Unsubscribed users open the Tuscan plan-selection panel directly.
                newButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.openPlans();
                }, true);
            }
        });
        
        // Handle subscription notices
        const subscriptionNotices = document.querySelectorAll('#subscriptionNotice, .subscription-notice');
        subscriptionNotices.forEach(notice => {
            if (this.isSubscribed) {
                notice.style.display = 'none';
            } else {
                notice.style.display = 'block';
            }
        });
    }
    
    relocateButtonToProfile(button, profileContainer, actionBarContainer) {
        if (!button || !profileContainer) {
            console.log("[BackendSubscriptionChecker] Cannot relocate - missing button or profile container");
            return;
        }
        
        // Check if button is already in profile container
        if (profileContainer.contains(button)) {
            console.log("[BackendSubscriptionChecker] Button already in profile container");
            profileContainer.style.display = 'block';
            return;
        }
        
        // Move button to profile container
        profileContainer.appendChild(button);
        profileContainer.style.display = 'block';
        
        // Add profile-specific styling class
        button.classList.add('profile-subscribe-btn');
        
        console.log("[BackendSubscriptionChecker] Button relocated to Profile screen");
    }
    
    relocateButtonToActionBar(button, profileContainer, actionBarContainer) {
        if (!button || !actionBarContainer) {
            console.log("[BackendSubscriptionChecker] Cannot relocate - missing button or action bar container");
            return;
        }
        
        // Check if button is already in action bar
        if (actionBarContainer.contains(button)) {
            console.log("[BackendSubscriptionChecker] Button already in action bar");
            return;
        }
        
        // Move button back to action bar (after the question counter)
        const questionCounter = actionBarContainer.querySelector('#question-counter');
        if (questionCounter) {
            questionCounter.after(button);
        } else {
            actionBarContainer.appendChild(button);
        }
        
        // Hide the profile container
        if (profileContainer) {
            profileContainer.style.display = 'none';
        }
        
        // Remove profile-specific styling class
        button.classList.remove('profile-subscribe-btn');
        
        console.log("[BackendSubscriptionChecker] Button relocated back to action bar");
    }
    
    updateQuestionCounter() {
        // Hide question counter UI elements for subscribed users
        const questionCounterElements = document.querySelectorAll(
            '.question-counter, #question-counter, #questionCounter, .questions-left, #questionsLeft, [data-question-counter], .question-count-display'
        );
        
        questionCounterElements.forEach(element => {
            if (this.isSubscribed) {
                // Hide the question counter for subscribed users
                element.style.display = 'none';
                console.log("[BackendSubscriptionChecker] Hiding question counter element:", element.id || element.className);
            } else {
                // Show the question counter for free users
                element.style.display = '';
                console.log("[BackendSubscriptionChecker] Showing question counter element:", element.id || element.className);
            }
        });
        
        // Also look for parent containers
        const questionCounterContainers = document.querySelectorAll(
            '.question-counter-container, .chat-limit-info, .free-tier-info'
        );
        
        questionCounterContainers.forEach(container => {
            if (this.isSubscribed) {
                container.style.display = 'none';
            } else {
                container.style.display = '';
            }
        });
        
        // Update any "Questions Left" text elements
        const questionsLeftElements = document.querySelectorAll('.questions-remaining, .questions-left-text');
        questionsLeftElements.forEach(element => {
            if (this.isSubscribed) {
                element.style.display = 'none';
            } else {
                element.style.display = '';
            }
        });
    }
    
    // ═════════════════════════════════════════════════════════════════════
    //  Tuscan-dusk subscription flow — the single source of truth.
    //  One plan-selection panel, one centered status panel, one checkout path.
    //  Identity always comes from the verified Firebase session server-side;
    //  the backend ignores any userId in the request body.
    // ═════════════════════════════════════════════════════════════════════

    // Display-only pricing. The real charge always comes from the Stripe price
    // referenced by STRIPE_MONTHLY_PRICE_ID / STRIPE_YEARLY_PRICE_ID; these
    // strings must be kept in step with those prices but never drive billing.
    static get PRICING() {
        return {
            // Yearly is framed per-month (Brilliant-style anchor). Founder
            // ruling 2026-08-24: the annual total is NOT shown on app surfaces;
            // "billed yearly" stays (cadence disclosure), and Stripe's hosted
            // checkout page itself states the full "$279.99/yr" before payment.
            monthly: { amount: '$34.99', cadence: '/mo', sub: 'Billed monthly · cancel anytime', cta: 'Go Premium' },
            yearly:  { amount: '$23.33', cadence: '/mo', sub: '7-day free trial · billed yearly', save: 'Save 33%', cta: 'Start 7-day free trial' }
        };
    }

    isAuthenticated() {
        try {
            if (window.firebaseAuth && window.firebaseAuth.currentUser) return true;
            if (typeof firebase !== 'undefined' && firebase.auth && firebase.auth().currentUser) return true;
        } catch (e) {}
        return false;
    }

    // Single entry point used by every trigger. Subscribers see status; others
    // see the plan picker.
    openSubscription() {
        if (this.isSubscribed) this.openStatus();
        else this.openPlans();
    }

    // ── modal plumbing (self-contained; does not rely on the inline
    //    index.html [data-close] handler, which only binds elements present
    //    at load and would miss these dynamically-injected panels) ──────────
    _closeSubModals() {
        document.querySelectorAll('.sub-modal-backdrop').forEach(el => {
            el.classList.remove('is-open');
            setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 320);
        });
        if (this._subEscHandler) {
            document.removeEventListener('keydown', this._subEscHandler);
            this._subEscHandler = null;
        }
    }

    _showSubModal(html) {
        this._closeSubModals();
        const wrap = document.createElement('div');
        wrap.innerHTML = html.trim();
        const backdrop = wrap.firstElementChild;
        document.body.appendChild(backdrop);

        // Close on the close button, on the backdrop itself, or on Escape.
        backdrop.addEventListener('click', (e) => {
            if (e.target === backdrop || (e.target.closest && e.target.closest('[data-sub-close]'))) {
                this._closeSubModals();
            }
        });
        this._subEscHandler = (e) => { if (e.key === 'Escape') this._closeSubModals(); };
        document.addEventListener('keydown', this._subEscHandler);

        // Force reflow so the .is-open transition runs from the closed state.
        void backdrop.offsetWidth;
        backdrop.classList.add('is-open');
        return backdrop;
    }

    _icon(name) {
        const I = {
            close: '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>',
            lock: '<rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path>',
            crown: '<path d="M3 7l4 5 5-7 5 7 4-5v11H3z"></path>',
            check: '<polyline points="20 6 9 17 4 12"></polyline>'
        };
        return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${I[name] || ''}</svg>`;
    }

    // ── Plan-selection panel (Free vs Premium + monthly / yearly toggle) ──
    openPlans() {
        const P = BackendSubscriptionChecker.PRICING;
        const html = `
        <div class="modal-backdrop sub-modal-backdrop" id="sub-plans-modal" role="dialog" aria-modal="true" aria-label="Choose your plan">
          <div class="modal subscribe-modal sub-plans">
            <button class="modal-close" data-sub-close aria-label="Close">${this._icon('close')}</button>
            <h2>Unlock the <em>full Lab</em></h2>
            <p>Read every book. Run unlimited debates. Get a personal tutor for every legend.</p>

            <div class="sub-billing" role="tablist" aria-label="Billing period">
              <button class="sub-billing-opt is-active" type="button" data-period="monthly" role="tab">Monthly</button>
              <button class="sub-billing-opt" type="button" data-period="yearly" role="tab">Yearly <span class="sub-billing-save">${P.yearly.save}</span></button>
            </div>

            <div class="sub-tiers">
              <div class="sub-tier">
                <div class="sub-tier-name">Free</div>
                <div class="sub-tier-price">$0<small>/forever</small></div>
                <div class="sub-tier-sub">A taste of the Lab</div>
                <ul class="sub-tier-feats">
                  <li>${this._icon('check')} Limited daily conversations</li>
                  <li>${this._icon('check')} Browse every legend</li>
                  <li>${this._icon('check')} Preview the library</li>
                </ul>
                <button class="sub-tier-cta sub-tier-cta-free" type="button" disabled>Your current plan</button>
              </div>
              <div class="sub-tier sub-tier-premium featured">
                <div class="best-value">Best value</div>
                <div class="sub-tier-name">Premium</div>
                <div class="sub-tier-price" id="sub-premium-price">${P.monthly.amount}<small>${P.monthly.cadence}</small></div>
                <div class="sub-tier-sub" id="sub-premium-sub">${P.monthly.sub}</div>
                <ul class="sub-tier-feats">
                  <li>${this._icon('check')} Unlimited conversations &amp; debates</li>
                  <li>${this._icon('check')} Full library — every page of every book</li>
                  <li>${this._icon('check')} Advanced Tutor with lesson paths</li>
                  <li>${this._icon('check')} All legends, priority responses</li>
                </ul>
                <button class="sub-tier-cta sub-tier-cta-premium" id="sub-go-premium" type="button" data-period="monthly">Go Premium</button>
              </div>
            </div>

            <p class="sub-secure">${this._icon('lock')} Secure payment powered by Stripe · cancel anytime</p>
          </div>
        </div>`;

        const backdrop = this._showSubModal(html);
        const priceEl = backdrop.querySelector('#sub-premium-price');
        const subEl = backdrop.querySelector('#sub-premium-sub');
        const goBtn = backdrop.querySelector('#sub-go-premium');

        backdrop.querySelectorAll('.sub-billing-opt').forEach(opt => {
            opt.addEventListener('click', () => {
                backdrop.querySelectorAll('.sub-billing-opt').forEach(o => o.classList.remove('is-active'));
                opt.classList.add('is-active');
                const period = opt.dataset.period === 'yearly' ? 'yearly' : 'monthly';
                const info = P[period];
                priceEl.innerHTML = `${info.amount}<small>${info.cadence}</small>`;
                subEl.textContent = info.sub;
                goBtn.dataset.period = period;
                goBtn.textContent = info.cta;
            });
        });
        goBtn.addEventListener('click', () => this.initiateCheckout(goBtn.dataset.period, goBtn));
    }

    // ── Centered "Premium Active" status panel ──
    openStatus() {
        const raw = this.subscriptionData && this.subscriptionData.status;
        const status = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Active';
        const html = `
        <div class="modal-backdrop sub-modal-backdrop" id="sub-status-modal" role="dialog" aria-modal="true" aria-label="Premium membership">
          <div class="modal sub-status-modal">
            <button class="modal-close" data-sub-close aria-label="Close">${this._icon('close')}</button>
            <div class="sub-status-badge">${this._icon('crown')} Premium Active</div>
            <h2>You're all <em>set</em>.</h2>
            <p>Your membership is active — the entire Lab is open to you.</p>
            <ul class="sub-status-feats">
              <li>${this._icon('check')} Unlimited conversations &amp; debates</li>
              <li>${this._icon('check')} Full library access</li>
              <li>${this._icon('check')} Advanced Tutor &amp; lesson paths</li>
              <li>${this._icon('check')} Every legend, priority responses</li>
            </ul>
            <div class="sub-status-meta"><span class="sub-status-dot"></span> Status: ${status}</div>
            <div class="sub-status-actions">
              <button class="btn btn-secondary" type="button" data-sub-close>Close</button>
              <button class="btn btn-primary" type="button" id="sub-manage"><span>Manage subscription</span></button>
            </div>
          </div>
        </div>`;

        const backdrop = this._showSubModal(html);
        const manage = backdrop.querySelector('#sub-manage');
        if (manage) manage.addEventListener('click', (e) => this.openCustomerPortal(e.currentTarget));
    }

    // ── Stripe Checkout: load price ids → create session → redirect ──
    async initiateCheckout(period, button) {
        period = (period === 'yearly') ? 'yearly' : 'monthly';

        // Checkout requires a verified server session. If the user isn't signed
        // in, remember the intent, open the sign-in modal, and resume on the
        // next auth-state change (see _resumePendingCheckout).
        if (!this.isAuthenticated()) {
            try { sessionStorage.setItem('llReopenPlansAfterAuth', '1'); } catch (e) {}
            this._closeSubModals();
            if (typeof window.showAuthModal === 'function') window.showAuthModal();
            else document.getElementById('modal-auth')?.classList.add('is-open');
            return;
        }

        const original = button ? button.innerHTML : null;
        if (button) { button.disabled = true; button.innerHTML = 'Redirecting…'; }

        try {
            const idsRes = await fetch('/api/price-ids', { credentials: 'include' });
            if (!idsRes.ok) throw new Error('Could not load plans right now');
            const ids = await idsRes.json();
            const priceId = ids[period];
            if (!priceId) throw new Error('That plan is not available right now');

            // The annual plan carries the 7-day free trial (Brilliant-style);
            // monthly remains an immediate charge. The paywall copy above must
            // always state this before the user reaches Stripe.
            const body = { priceId };
            if (period === 'yearly') body.trial = true;
            const res = await fetch('/create-checkout-session', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                if (res.status === 401) throw new Error('Please sign in to continue');
                throw new Error(err.error || `Checkout failed (${res.status})`);
            }
            const data = await res.json();
            if (!data.checkoutUrl) throw new Error('No checkout URL received');
            window.location.href = data.checkoutUrl;
        } catch (e) {
            console.error('[BackendSubscriptionChecker] Checkout error:', e);
            if (button) { button.disabled = false; button.innerHTML = original; }
            this.showToast(e.message || 'Could not start checkout. Please try again.', 'error');
        }
    }

    // Resume a checkout that was interrupted by the sign-in gate.
    _resumePendingCheckout() {
        try {
            if (sessionStorage.getItem('llReopenPlansAfterAuth') === '1') {
                sessionStorage.removeItem('llReopenPlansAfterAuth');
                if (!this.isSubscribed) setTimeout(() => this.openPlans(), 350);
            }
        } catch (e) {}
    }

    // Handle the redirect back from Stripe Checkout (success / cancel).
    handleReturnFromCheckout() {
        let params;
        try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
        const clean = () => {
            try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) {}
        };

        if (params.has('subscription_success')) {
            clean();
            // The X (Twitter) Purchase conversion now fires from the shared
            // tracking include (templates/partials/tracking.html), which runs in
            // <head> on EVERY user-facing page — including /learn* checkout
            // returns that never load this bundle — with the REAL plan
            // value/currency from /api/checkout-session-info and a per-session
            // dedupe conversion_id. It reads the URL before this clean() strips
            // it. (The old call here reused the character-click event ID with a
            // hardcoded value, so Purchase never actually recorded.)
            this.showToast('Welcome, Patron — your membership is active.', 'success');
            this._showPatronWelcome();
            this._pollForSubscription();
        } else if (params.has('subscription_cancelled')) {
            clean();
            this.showToast('Checkout cancelled — you can upgrade anytime.', 'info');
        }
    }

    // After a successful checkout the webhook may take a moment to land; poll
    // a few times so the UI flips to subscribed without a manual refresh.
    _pollForSubscription() {
        let n = 0;
        const tick = async () => {
            n++;
            await this.checkSubscriptionStatus();
            if (this.isSubscribed || n >= 12) return;
            setTimeout(tick, 5000);
        };
        setTimeout(tick, 2500);
    }

    _showPatronWelcome() {
        try { document.body.classList.add('is-patron'); } catch (e) {}
        const w = document.getElementById('welcome-patron');
        if (w) {
            w.classList.add('show');
            setTimeout(() => w.classList.remove('show'), 3200);
        }
    }

    // On-brand toast (replaces the old Bootstrap toasts/alerts).
    showToast(message, type = 'info') {
        const existing = document.getElementById('sub-toast');
        if (existing) existing.remove();
        const el = document.createElement('div');
        el.id = 'sub-toast';
        el.className = `sub-toast sub-toast-${type}`;
        el.setAttribute('role', 'status');
        el.textContent = message;
        document.body.appendChild(el);
        void el.offsetWidth;
        el.classList.add('is-open');
        setTimeout(() => {
            el.classList.remove('is-open');
            setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 350);
        }, 4200);
    }
    
    async openCustomerPortal(button) {
        console.log("[BackendSubscriptionChecker] Opening customer portal...");
        
        // Store original button content
        const originalContent = button.innerHTML;
        const span = button.querySelector('span');
        
        try {
            // Add loading state
            button.disabled = true;
            button.innerHTML = '<i class="fas fa-spinner fa-spin"></i> <span>Loading...</span>';
            
            // Prepare request body with Firebase UID if available
            const requestBody = {};
            if (this.userId) {
                requestBody.userId = this.userId;
                console.log("[BackendSubscriptionChecker] Sending Firebase UID:", this.userId);
            }
            
            // Make POST request to create customer portal session
            const response = await fetch('/create-customer-portal-session', {
                method: 'POST',
                credentials: 'include',  // Changed from 'same-origin' to 'include' for Safari compatibility
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Failed to create portal session (${response.status})`);
            }
            
            const data = await response.json();
            
            if (!data.url) {
                throw new Error('No portal URL received from server');
            }
            
            console.log("[BackendSubscriptionChecker] Customer portal URL received, navigating to portal");
            
            // Navigate to the portal URL in the current tab
            window.location.href = data.url;
            
            // Restore original button after a short delay
            setTimeout(() => {
                button.innerHTML = originalContent;
                button.disabled = false;
            }, 2000);
            
        } catch (error) {
            console.error("[BackendSubscriptionChecker] Error opening customer portal:", error);
            
            // Show error state on button
            button.innerHTML = '<i class="fas fa-exclamation-triangle"></i> <span>Error</span>';
            button.classList.add('btn-danger');
            button.classList.remove('btn-translucent');
            
            // Show error message to user
            const errorMessage = error.message || 'Failed to open customer portal. Please try again later.';
            
            // If bootstrap is available, show a toast
            if (typeof bootstrap !== 'undefined' && bootstrap.Toast) {
                const toastHTML = `
                    <div class="toast position-fixed top-0 end-0 m-3" role="alert" aria-live="assertive" aria-atomic="true" data-bs-autohide="true" data-bs-delay="5000">
                        <div class="toast-header bg-danger text-white">
                            <i class="fas fa-exclamation-circle me-2"></i>
                            <strong class="me-auto">Error</strong>
                            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast" aria-label="Close"></button>
                        </div>
                        <div class="toast-body">
                            ${errorMessage}
                        </div>
                    </div>
                `;
                
                // Add toast to body
                const toastContainer = document.createElement('div');
                toastContainer.innerHTML = toastHTML;
                document.body.appendChild(toastContainer.firstElementChild);
                
                // Show the toast
                const toastElement = document.querySelector('.toast:last-child');
                const toast = new bootstrap.Toast(toastElement);
                toast.show();
                
                // Remove toast from DOM after it's hidden
                toastElement.addEventListener('hidden.bs.toast', () => {
                    toastElement.remove();
                });
            } else {
                // Fallback to alert if bootstrap isn't available
                alert(errorMessage);
            }
            
            // Restore original button after delay
            setTimeout(() => {
                button.innerHTML = originalContent;
                button.disabled = false;
                button.classList.remove('btn-danger');
                button.classList.add('btn-translucent');
            }, 3000);
        }
    }
    
    // Backwards-compatible alias. The old left-docked Bootstrap #subscribedModal
    // is gone; the centered Tuscan status panel is the single status surface now.
    showSubscribedMessage() {
        this.openStatus();
    }
    
    // Close the auth modal (Profile UI)
    closeAuthModal() {
        // Find and close the auth modal
        const authModal = document.getElementById('authModal');
        if (authModal) {
            // Hide the modal using display style
            authModal.style.display = 'none';
            console.log("[BackendSubscriptionChecker] Auth modal closed");
        }
        
        // Also try to close using the auth modal's close function if available
        const authCloseBtn = document.querySelector('.auth-close');
        if (authCloseBtn) {
            authCloseBtn.click();
        }
    }
    
    // Public method to check if user is subscribed
    getSubscriptionStatus() {
        return {
            isSubscribed: this.isSubscribed,
            subscriptionData: this.subscriptionData,
            userId: this.userId
        };
    }
    
    // Method to force refresh subscription status
    async refreshStatus() {
        await this.checkSubscriptionStatus();
    }
}

// Create singleton instance
const backendSubscriptionChecker = new BackendSubscriptionChecker();

// Export both the class and the instance
export default backendSubscriptionChecker;

// Also make it available globally for debugging
window.backendSubscriptionChecker = backendSubscriptionChecker;

// Single global entry point used by every trigger in templates/index.html
// (action-bar button, mobile wheel, profile menu, question counter, locked
// volume). Routes to the status panel for subscribers, else the plan panel.
window.openSubscriptionFlow = () => backendSubscriptionChecker.openSubscription();